import { useState, useCallback, useRef } from 'react'
import { logger } from '@/lib/logger'

export interface BootPhase {
  id: string
  name: string
  description: string
  icon: string
  duration: number // estimated duration in ms
  progress: number // 0-100
  status: 'pending' | 'active' | 'completed' | 'error'
  startTime?: number
  endTime?: number
}

export interface BootMetrics {
  totalTime: number
  phaseTimes: Record<string, number>
  errors: string[]
  warnings: string[]
  performance: {
    audioContextInit: number
    workletLoad: number
    neuralEngineInit: number
    componentLoad: number
  }
}

export const useBootPhaseManager = (initialPhases: BootPhase[]) => {
  const [phases, setPhases] = useState<BootPhase[]>(initialPhases)
  const [currentPhase, setCurrentPhase] = useState<string>('browser_check')
  const [bootMetrics, setBootMetrics] = useState<BootMetrics>({
    totalTime: 0,
    phaseTimes: {},
    errors: [],
    warnings: [],
    performance: {
      audioContextInit: 0,
      workletLoad: 0,
      neuralEngineInit: 0,
      componentLoad: 0
    }
  })

  const startTimeRef = useRef<number>(Date.now())
  const animationFrameRef = useRef<number | null>(null)
  const retryCountsRef = useRef<Record<string, number>>({})

  // Update phase progress with smooth animation
  const updatePhaseProgress = useCallback((phaseId: string, progress: number, status?: BootPhase['status']) => {
    setPhases(prev => prev.map(phase => {
      if (phase.id === phaseId) {
        const updatedPhase = { ...phase, progress: Math.min(100, Math.max(0, progress)) }
        if (status) {
          updatedPhase.status = status
          if (status === 'active' && !updatedPhase.startTime) {
            updatedPhase.startTime = Date.now()
          } else if (status === 'completed' && !updatedPhase.endTime) {
            updatedPhase.endTime = Date.now()
          }
        }
        return updatedPhase
      }
      return phase
    }))
  }, [])

  // Simulate progress for visual feedback
  const animateProgress = useCallback((phaseId: string, targetProgress: number, duration: number = 300) => {
    const startTime = Date.now()

    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(targetProgress, elapsed / duration * targetProgress)

      updatePhaseProgress(phaseId, progress)

      if (progress < targetProgress) {
        animationFrameRef.current = requestAnimationFrame(animate)
      }
    }

    animate()
  }, [updatePhaseProgress])

  // Retry mechanism for critical phases
  const retryPhase = useCallback(async (phaseId: string, phaseFunction: () => Promise<void>, maxRetries = 2) => {
    const currentRetries = retryCountsRef.current[phaseId] || 0

    if (currentRetries >= maxRetries) {
      logger.error(`Phase ${phaseId} failed after ${maxRetries} retries`)
      setBootMetrics(prev => ({
        ...prev,
        errors: [...prev.errors, `Phase ${phaseId} failed after ${maxRetries} retries`]
      }))
      return false
    }

    try {
      retryCountsRef.current[phaseId] = currentRetries + 1
      logger.info(`Retrying phase ${phaseId} (attempt ${currentRetries + 1}/${maxRetries + 1})`)
      await phaseFunction()
      return true
    } catch (error) {
      logger.warn(`Phase ${phaseId} retry ${currentRetries + 1} failed:`, error)
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, 1000))
      return retryPhase(phaseId, phaseFunction, maxRetries)
    }
  }, [])

  const addError = useCallback((error: string) => {
    setBootMetrics(prev => ({
      ...prev,
      errors: [...prev.errors, error]
    }))
  }, [])

  const addWarning = useCallback((warning: string) => {
    setBootMetrics(prev => ({
      ...prev,
      warnings: [...prev.warnings, warning]
    }))
  }, [])

  const completePhase = useCallback((phaseId: string) => {
    updatePhaseProgress(phaseId, 100, 'completed')
    const phaseTime = Date.now() - (phases.find(p => p.id === phaseId)?.startTime || Date.now())
    setBootMetrics(prev => ({
      ...prev,
      phaseTimes: { ...prev.phaseTimes, [phaseId]: phaseTime }
    }))
  }, [updatePhaseProgress, phases])

  const updateCurrentPhase = useCallback((phaseId: string) => {
    setCurrentPhase(phaseId)
    updatePhaseProgress(phaseId, 0, 'active')
  }, [updatePhaseProgress])

  return {
    phases,
    currentPhase,
    bootMetrics,
    updatePhaseProgress,
    animateProgress,
    retryPhase,
    addError,
    addWarning,
    completePhase,
    updateCurrentPhase
  }
}